package diana.soleil.design.iraniancanadienbazzar.feature.events

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.QuantityStepper
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.StatusBadge
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.Ticket
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketStatus
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_claim_cta
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_claim_failed
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_claim_title
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_error
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_free_note
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_party_size
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_qr_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_qty_label
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_retry
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_sold_out
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_status_refunded
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_status_used
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_status_valid
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_wallet_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_wallet_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.tickets_wallet_title
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

// ---------- Free-ticket claim (Stitch 78431a7c, free-RSVP variant — no payment steps by design) ----------

@Composable
fun EventTicketsRoute(
    eventId: String,
    onBack: () -> Unit,
    onIssued: (ticketId: String) -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: EventTicketsViewModel = koinViewModel(parameters = { parametersOf(eventId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    LaunchedEffect(state.issuedTicketId, state.signInRequired) {
        state.issuedTicketId?.let { viewModel.consumeGates(); onIssued(it) }
        if (state.signInRequired) { viewModel.consumeGates(); onRequireSignIn() }
    }
    Column(modifier = modifier.fillMaxHeight().background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidthCompact)) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().navigationBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.tickets_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.tickets_claim_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
            )
        }
        when {
            state.loading -> LoadingState()
            state.event == null -> ErrorState(
                message = stringResource(Res.string.tickets_error),
                onRetry = viewModel::load,
                retryLabel = stringResource(Res.string.tickets_retry),
            )
            else -> Column(
                modifier = Modifier.padding(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                val event = state.event ?: return@Column
                Text(
                    text = event.title.resolve(AppTheme.language).orEmpty(),
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                )
                Text(
                    text = stringResource(Res.string.tickets_free_note),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppTheme.colors.surfaceContainer, AppTheme.shapes.md)
                        .padding(AppTheme.spacing.md),
                )
                if (state.types.isNotEmpty()) {
                    Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                        state.types.forEach { type ->
                            AppChip(
                                label = if (type.soldOut) {
                                    stringResource(Res.string.tickets_sold_out, type.name)
                                } else type.name,
                                selected = state.selectedTypeId == type.id,
                                onClick = if (type.soldOut) null else ({ viewModel.select(type.id) }),
                            )
                        }
                    }
                }
                Text(
                    text = stringResource(Res.string.tickets_qty_label),
                    style = AppTheme.typography.labelMd,
                    color = AppTheme.colors.onSurfaceVariant,
                )
                QuantityStepper(quantity = state.qty, onQuantityChange = viewModel::setQty, max = 4)
                if (state.failed) {
                    Text(
                        text = stringResource(Res.string.tickets_claim_failed),
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onErrorContainer,
                    )
                }
                AppButton(
                    text = stringResource(Res.string.tickets_claim_cta),
                    onClick = viewModel::claim,
                    modifier = Modifier.fillMaxWidth(),
                    enabled = state.selectedTypeId != null && !state.claiming,
                    loading = state.claiming,
                    leadingIcon = "confirmation_number",
                )
            }
        }
    }
}

// ---------- Ticket wallet + digital ticket QR (Stitch c162e3ff) ----------

@Composable
fun MyTicketsRoute(
    onBack: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    highlightTicketId: String? = null,
    viewModel: MyTicketsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    LaunchedEffect(state.signInRequired) {
        if (state.signInRequired) onRequireSignIn()
    }
    Column(modifier = modifier.fillMaxHeight().background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidth)) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().navigationBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.tickets_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.tickets_wallet_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
            )
        }
        when {
            state.loading -> LoadingState()
            state.error -> ErrorState(
                message = stringResource(Res.string.tickets_error),
                onRetry = viewModel::retry,
                retryLabel = stringResource(Res.string.tickets_retry),
            )
            state.tickets.isEmpty() -> EmptyState(
                title = stringResource(Res.string.tickets_wallet_empty_title),
                description = stringResource(Res.string.tickets_wallet_empty_desc),
            )
            else -> {
                // Soonest events first; a just-issued ticket still floats to the very top so the
                // confirmation lands on it.
                val ordered = state.tickets
                    .sortedBy { it.startsAtMillis ?: Long.MAX_VALUE }
                    .sortedByDescending { it.id == highlightTicketId }
                LazyColumn(
                    contentPadding = PaddingValues(AppTheme.spacing.lg),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                ) {
                    items(count = ordered.size, key = { ordered[it].id }) { index ->
                        DigitalTicketCard(ticket = ordered[index])
                    }
                }
            }
        }
    }
}

/** One digital ticket: event identity + party size + scannable QR (Stitch `c162e3ff`). */
@Composable
private fun DigitalTicketCard(ticket: Ticket) {
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = ticket.eventTitle.resolve(AppTheme.language).orEmpty(),
                    style = AppTheme.typography.bodyLg,
                    color = AppTheme.colors.onSurface,
                )
                StatusBadge(
                    label = stringResource(
                        when (ticket.status) {
                            TicketStatus.Valid -> Res.string.tickets_status_valid
                            TicketStatus.Used -> Res.string.tickets_status_used
                            TicketStatus.Refunded -> Res.string.tickets_status_refunded
                        },
                    ),
                )
            }
            // Lead with the event date/time — a ticket without it is nearly useless (the field was
            // snapshotted onto the ticket but never shown).
            ticket.startsAtMillis?.let { millis ->
                Text(
                    text = eventDateTime(millis),
                    style = AppTheme.typography.labelMd,
                    color = AppTheme.colors.primary,
                )
            }
            if (ticket.venueName.isNotBlank()) {
                Text(
                    text = ticket.venueName,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }
            Text(
                text = stringResource(Res.string.tickets_party_size, ticket.qty),
                style = AppTheme.typography.labelMd,
                color = AppTheme.colors.primary,
            )
            Box(
                modifier = Modifier
                    .background(AppTheme.colors.surface, AppTheme.shapes.card)
                    .padding(AppTheme.spacing.md),
            ) {
                Image(
                    painter = rememberQrCodePainter(ticket.qrPayload),
                    contentDescription = stringResource(Res.string.tickets_qr_a11y),
                    modifier = Modifier.size(AppTheme.sizes.qrCode),
                )
            }
            Text(
                text = ticket.id,
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )
        }
    }
}
